import { createContext, useContext, useState, useCallback } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { signIn } from "services/auth";

/**
 * Route-based admin auth. The session is held in React state (persisted to
 * localStorage so a refresh keeps you signed in) and exposed through useAuth().
 * Protected routes are wrapped in <RequireAuth>, which redirects to the real
 * /admin/login route — no in-component "authed" toggles.
 */

const STORAGE_KEY = "kcgs_admin_session";
const AuthContext = createContext(null);

function readSession() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(readSession);

  const login = useCallback(async (username, password) => {
    const result = await signIn({ username, password });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(result));
    setSession(result);
    return result;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setSession(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user: session?.user || null, isAuthed: !!session, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}

/** Guards admin routes: bounces to the login route, remembering where you were. */
export function RequireAuth({ children }) {
  const { isAuthed } = useAuth();
  const location = useLocation();
  if (!isAuthed) {
    return <Navigate to="/admin/login" replace state={{ from: location }} />;
  }
  return children;
}
